import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  Clock,
  Coins,
  ExternalLink,
  FileText,
  Heart,
  Link2,
  Lock,
  X,
} from "lucide-react";
import { isOutcomeTradable } from "../../utils/bondPredictDetailHelpers";
import MarketHeroAvatar from "../../../components/market/MarketHeroAvatar";
import ContractLineAvatar from "../../../components/market/ContractLineAvatar";
import {
  formatChancePct,
  formatPrice,
  formatShortCountdown,
  formatVolume,
  getBinaryOdds,
  getH2HLabels,
  getMarketTitle,
  getOutcomeDisplayPrice,
  groupOutcomesBySelection,
  MARKET_TYPES,
  STATUS_META,
  TYPE_META,
} from "../../utils/bondPredictHelpers";

const PredictMarketInspector = ({
  market,
  isLiked,
  likeBusy,
  onClose,
  onToggleLike,
  onOpenMarket,
  onOutcomeClick,
  userId,
}) => {
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    if (!market?.timings?.closeAt) {
      setCountdown("");
      return undefined;
    }
    const status = market?.status || "OPEN";
    const tick = () =>
      setCountdown(formatShortCountdown(market.timings.closeAt, status));
    tick();
    const id = window.setInterval(tick, 30000);
    return () => window.clearInterval(id);
  }, [market]);

  const grouped = useMemo(
    () => groupOutcomesBySelection(market?.outcomes || []),
    [market?.outcomes]
  );

  if (!market) {
    return (
      <aside className="bp-inspector bp-inspector-empty">
        <div className="bp-inspector-empty-inner">
          <div className="bp-inspector-empty-icon">
            <Activity size={22} strokeWidth={2} />
          </div>
          <h2>Select a market</h2>
          <p>
            Pick any contract from the board or live tape to preview odds, volume, and
            resolution details.
          </p>
        </div>
      </aside>
    );
  }

  const title = getMarketTitle(market);
  const type = market?.type || MARKET_TYPES.BINARY;
  const status = market?.status || "OPEN";
  const statusMeta = STATUS_META[status] || { label: status, tone: "muted" };
  const typeMeta = TYPE_META[type] || TYPE_META.BINARY;
  const volume = market?.metrics?.totalVolume || 0;
  const trades = market?.metrics?.totalTrades || 0;
  const likes = market?.likes?.count || 0;
  const description = market?.description || "";
  const rules = market?.rules || "";
  const source = market?.resolutionSource || "";

  const handleOutcomePick = (outcome) => {
    if (!outcome?._id) return;
    if (!userId) return;
    if (!isOutcomeTradable(market, outcome)) return;
    onOutcomeClick?.(outcome);
  };

  const outcomeBtnProps = (outcome, variant = "") => {
    const tradable = isOutcomeTradable(market, outcome);
    const disabled = !tradable || !userId;
    return {
      type: "button",
      disabled,
      title: !userId
        ? "Sign in to trade"
        : !tradable
          ? "Trading not available"
          : "Trade this outcome",
      onClick: () => handleOutcomePick(outcome),
      className: `bp-inspector-outcome-btn ${variant}${disabled ? " bp-inspector-outcome-btn-disabled" : ""}`.trim(),
    };
  };

  const renderOdds = () => {
    if (type === MARKET_TYPES.BINARY) {
      const { yes, no, yesPrice, noPrice } = getBinaryOdds(market);
      if (!yes || !no) return null;
      return (
        <div className="bp-inspector-odds bp-inspector-odds-binary">
          <button {...outcomeBtnProps(yes, "bp-inspector-outcome-btn-yes")}>
            <span>Yes</span>
            <strong>{formatChancePct(yesPrice)}</strong>
            {!isOutcomeTradable(market, yes) ? <Lock size={11} aria-hidden /> : null}
          </button>
          <button {...outcomeBtnProps(no, "bp-inspector-outcome-btn-no")}>
            <span>No</span>
            <strong>{formatChancePct(noPrice)}</strong>
            {!isOutcomeTradable(market, no) ? <Lock size={11} aria-hidden /> : null}
          </button>
        </div>
      );
    }
    if (type === MARKET_TYPES.HEAD_TO_HEAD) {
      const h2h = getH2HLabels(market);
      const g = grouped.find((x) => x.yes && x.no);
      if (!h2h || !g) return null;
      return (
        <div className="bp-inspector-odds bp-inspector-odds-h2h">
          <button {...outcomeBtnProps(g.yes, "bp-inspector-outcome-btn-h2h")}>
            <span>{h2h.labelA}</span>
            <strong>{formatPrice(h2h.priceA)}</strong>
          </button>
          <span className="bp-inspector-h2h-vs">{h2h.fxStatus}</span>
          <button {...outcomeBtnProps(g.no, "bp-inspector-outcome-btn-h2h")}>
            <span>{h2h.labelB}</span>
            <strong>{formatPrice(h2h.priceB)}</strong>
          </button>
        </div>
      );
    }
    const lines = grouped.filter((g) => g.yes || g.no).slice(0, 6);
    return (
      <div className="bp-inspector-multi-list">
        {lines.map((g) => {
          const outcome = g.yes || g.no;
          const label = (g.yes?.label || g.no?.label || g.selectionKey || "").replace(
            /\s*\((Yes|No)\)\s*$/i,
            ""
          );
          const tradable = isOutcomeTradable(market, outcome);
          return (
            <button
              key={g.selectionKey}
              {...outcomeBtnProps(outcome, "bp-inspector-outcome-btn-row")}
            >
              <ContractLineAvatar
                market={market}
                selectionKey={g.selectionKey}
                label={label}
                size={20}
                className="bp-inspector-outcome-thumb"
              />
              <span>{label}</span>
              <strong>{formatPrice(getOutcomeDisplayPrice(outcome))}</strong>
              {!tradable ? <Lock size={11} aria-hidden /> : null}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <aside className="bp-inspector">
      <div className="bp-inspector-head">
        <button type="button" className="bp-icon-btn" onClick={onClose} aria-label="Close preview">
          <X size={14} />
        </button>
        <span className={`bp-status-pill bp-status-${statusMeta.tone}`}>
          <span className="bp-status-dot" />
          {statusMeta.label}
        </span>
      </div>

      <div className="bp-inspector-top">
        <div className="bp-inspector-hero">
          {type !== MARKET_TYPES.HEAD_TO_HEAD ? (
            <MarketHeroAvatar market={market} label={title} size={44} className="bp-inspector-avatar" />
          ) : null}
          <div>
            <p className="bp-inspector-type">{typeMeta.label}</p>
            <h2>{title}</h2>
          </div>
        </div>
      </div>

      <div className="bp-inspector-body">
        <div className="bp-inspector-stats">
          <div className="bp-inspector-stat">
            <Coins size={12} />
            <span>{formatVolume(volume)} vol</span>
          </div>
          <div className="bp-inspector-stat">
            <Activity size={12} />
            <span>{trades} trades</span>
          </div>
          {countdown ? (
            <div className="bp-inspector-stat bp-inspector-stat-time">
              <Clock size={12} />
              <span>{countdown}</span>
            </div>
          ) : null}
        </div>

        <div className="bp-inspector-section">
          <p className="bp-inspector-section-label">
            Live odds
            {userId ? <em className="bp-inspector-section-hint">Tap an outcome to trade</em> : null}
          </p>
          {renderOdds()}
        </div>

        {description ? (
          <div className="bp-inspector-section">
            <p className="bp-inspector-section-label">
              <FileText size={11} />
              About
            </p>
            <p className="bp-inspector-copy">{description}</p>
          </div>
        ) : null}

        {source ? (
          <div className="bp-inspector-section">
            <p className="bp-inspector-section-label">
              <Link2 size={11} />
              Resolution source
            </p>
            <p className="bp-inspector-copy">{source}</p>
          </div>
        ) : null}

        {rules ? (
          <div className="bp-inspector-section">
            <p className="bp-inspector-section-label">Rules</p>
            <p className="bp-inspector-copy">{rules}</p>
          </div>
        ) : null}
      </div>

      <div className="bp-inspector-actions">
        <button
          type="button"
          className={`bp-like-btn bp-like-btn-wide${isLiked ? " bp-like-btn-on" : ""}`}
          disabled={likeBusy}
          onClick={() => onToggleLike?.(market)}
          aria-label={isLiked ? "Unlike market" : "Like market"}
        >
          <Heart size={14} fill={isLiked ? "currentColor" : "none"} />
          {isLiked ? "Liked" : "Like"}
          {likes > 0 ? <span className="bp-like-count">{likes}</span> : null}
        </button>
        <button
          type="button"
          className="bp-port-open-market-btn"
          onClick={() => onOpenMarket?.(market)}
        >
          <ExternalLink size={13} />
          Open market
        </button>
      </div>
    </aside>
  );
};

export default PredictMarketInspector;
